import { DEFAULT_ITEM_HEIGHT, NUMBER_OF_ITEMS, SEARCH_BAR_HEIGHT_WITH_PADDING } from './constants';
import FilterList, { Props as FilterListProps } from './index';
import { BaseProps } from '#components';
import { Box, Paper, Popover, PopoverProps } from '@mui/material';
import { max } from 'lodash';
import { PropsWithChildren, useRef } from 'react';

export type Props<TData> = BaseProps &
  FilterListProps<TData> &
  Pick<PopoverProps, 'open' | 'PaperProps'> & {
    onPopoverClose?: PopoverProps['onClose'];
    listChildren?: FilterListProps<TData>['children'];
    footer?: React.ReactNode;
    getListHeight?: (options: TData[]) => number;
    fullWidth?: boolean;
  };

const FilterListPopover = <TData,>({
  dataTestId,
  open,
  PaperProps,
  onPopoverClose,
  listChildren,
  footer,
  children,
  options,
  getListHeight,
  fullWidth,
  ...filterListProps
}: PropsWithChildren<Props<TData>>): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const defaultGetListHeight = () => {
    if (options.length > NUMBER_OF_ITEMS) {
      return NUMBER_OF_ITEMS * DEFAULT_ITEM_HEIGHT;
    }
    return options.length * DEFAULT_ITEM_HEIGHT + SEARCH_BAR_HEIGHT_WITH_PADDING;
  };

  return (
    <>
      <Box
        ref={wrapperRef}
        data-testid={dataTestId}
        // eslint-disable-next-line no-restricted-syntax
        sx={{ width: fullWidth ? '100%' : undefined }}
      >
        {children}
      </Box>
      <Popover
        open={open}
        anchorEl={wrapperRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        slotProps={{
          paper: PaperProps ?? {
            elevation: 2,
            // Set the width of the popover to the width of the wrapper or 300px, whichever is greater
            // so we don't have a popover that's too small
            style: { width: max([wrapperRef.current?.clientWidth ?? 0, 300]) },
          },
        }}
        onClose={onPopoverClose}
        disableScrollLock
      >
        <Paper>
          <Box p={1}>
            <FilterList {...filterListProps} options={options} getListHeight={getListHeight ?? defaultGetListHeight}>
              {listChildren}
            </FilterList>
          </Box>
          {footer}
        </Paper>
      </Popover>
    </>
  );
};

export default FilterListPopover;
